import { DrawingMode, ShapeKind, LineType, LineCaps, FontType, Stamp } from '../types';
import { MultiroleLinkCreateInfo } from '../../../../../common/src/ts/types/multiroleLink';
import DrawingToolAction from '../actions/DrawingToolAction';
import BlobImageLoader from '../util/BlobImageLoader';
import { ImageEntry } from '../actions/DrawingAction';
import { fundamentalSizeMultiplier } from '../constants';

export interface DrawingToolData {
  readonly stampImageCaches: { [path: string]: StampImageState };
  readonly mode: DrawingMode;
  readonly visibleShapeKinds: ShapeKind[];
  readonly showHandwriting: boolean;
  readonly showObjects: boolean;
  readonly showLearningPenObjects: boolean;
  readonly drawingAttrs: DrawingAttrs;
  readonly alpha: number;
}

export interface DrawingToolState extends DrawingToolData {
  readonly imageFileToPaste: File | null;
  readonly pastingImages: ReadonlyArray<PastingImage>;
  readonly multiroleLinkToPaste: MultiroleLinkCreateInfo | null;
  readonly stampImageCaches: { [path: string]: StampImageState };
}

export const defaultLineCaps: LineCaps = {
  [LineType.Solid]: 'butt',
  [LineType.Dotted]: 'butt',
  [LineType.Dashed]: 'butt',
  [LineType.None]: 'butt',
};

export const solidRoundLineCaps: LineCaps = {
  [LineType.Solid]: 'round',
  [LineType.Dotted]: 'butt',
  [LineType.Dashed]: 'butt',
  [LineType.None]: 'butt',
};

export interface DrawingAttr {
  readonly fillColor: string;
  readonly lineColor: string;
  readonly lineWidth: number;
  readonly lineType: LineType;
  readonly lineCaps: LineCaps;
  readonly isTransparent: boolean;
  readonly isFilled: boolean;
  readonly textColor: string;
  readonly fontSize: number;
  readonly fontType: FontType;
  readonly stamp?: Stamp;
  readonly lineHeight: number;
  readonly padding: number;
  readonly stampPath?: string;
}

const defaultDrawingAttr: DrawingAttr = {
  fillColor: 'black',
  lineColor: 'black',
  lineWidth: 3 * fundamentalSizeMultiplier,
  lineType: LineType.Solid,
  lineCaps: defaultLineCaps,
  isTransparent: false,
  isFilled: false,
  textColor: 'black',
  fontSize: 100,
  fontType: FontType.Gothic,
  lineHeight: 1,
  padding: 1,
  stampPath: '',
};

const solidRoundLineCapsDrawingAttr: DrawingAttr = {
  ...defaultDrawingAttr, lineCaps: solidRoundLineCaps,
};

const noteDrawingAttr: DrawingAttr = {
  ...defaultDrawingAttr,
  fillColor: 'yellow',
  isTransparent: true,
  isFilled: true,
};

export type DrawingAttrs = { readonly [index: number]: DrawingAttr };

const defaultDrawingAttrs: DrawingAttrs = {
  [DrawingMode.Handwriting]: solidRoundLineCapsDrawingAttr,
  [DrawingMode.Handerasing]: defaultDrawingAttr,
  [DrawingMode.VerticalStraightLineHandWriting]: solidRoundLineCapsDrawingAttr,
  [DrawingMode.HorizontalStraightLineHandWriting]: solidRoundLineCapsDrawingAttr,
  [DrawingMode.RectDrawing]: defaultDrawingAttr,
  [DrawingMode.SquareDrawing]: defaultDrawingAttr,
  [DrawingMode.OvalDrawing]: solidRoundLineCapsDrawingAttr,
  [DrawingMode.CircleDrawing]: solidRoundLineCapsDrawingAttr,
  [DrawingMode.TriangleDrawing]: defaultDrawingAttr,
  [DrawingMode.EquilateralTriangleDrawing]: defaultDrawingAttr,
  [DrawingMode.StraightLineDrawing]: defaultDrawingAttr,
  [DrawingMode.AxisAlignedStraightLineDrawing]: defaultDrawingAttr,
  [DrawingMode.ImagePasting]: defaultDrawingAttr,
  [DrawingMode.StickyDrawing]: noteDrawingAttr,
  [DrawingMode.BalloonDrawing]: noteDrawingAttr,
  [DrawingMode.MultiVertexDrawing]: defaultDrawingAttr,
  [DrawingMode.BalloonArrowMoving]: defaultDrawingAttr,
  [DrawingMode.Stamp]: defaultDrawingAttr,
  [DrawingMode.LearningPenRect]: defaultDrawingAttr,
};

export enum PastingImageState {
  Loading,
  LoadingWithCallback,
  Ready,
  Pasted,
  Error,
}

interface LoadingStatePastingImage {
  readonly state: PastingImageState.Loading;
  readonly source: Blob;
  readonly loader: BlobImageLoader;
}

interface LoadingWithCallbackStatePastingImage {
  readonly state: PastingImageState.LoadingWithCallback;
  readonly source: Blob;
  readonly loader: BlobImageLoader;
  readonly callback: (entry: ImageEntry) => void;
}

interface ReadyStatePastingImage {
  readonly state: PastingImageState.Ready;
  readonly source: Blob;
  readonly texture: HTMLCanvasElement;
}

interface PastedStatePastingImage {
  readonly state: PastingImageState.Pasted;
  readonly source: Blob;
  readonly entry: ImageEntry;
}

interface ErrorStatePastingImage {
  readonly state: PastingImageState.Error;
  readonly source: Blob;
}

export interface StampImageState {
  readonly entry: ImageEntry;
  loaded: boolean;
  loadPromise?: Promise<StampImageState>;
}

export type PastingImage =
  LoadingStatePastingImage |
  LoadingWithCallbackStatePastingImage |
  ReadyStatePastingImage |
  PastedStatePastingImage |
  ErrorStatePastingImage;

export const initDrawingToolState = (): DrawingToolState => ({
  mode: DrawingMode.Handwriting,
  imageFileToPaste: null,
  pastingImages: [],
  stampImageCaches: {},
  visibleShapeKinds: initVisibleShapeKinds(),
  showHandwriting: true,
  showObjects: true,
  showLearningPenObjects: true,
  drawingAttrs: defaultDrawingAttrs,
  multiroleLinkToPaste: null,
  alpha: 0.5,
});

export const jsonizeDrawingToolState = (source: DrawingToolState): DrawingToolData => {
  const { pastingImages, imageFileToPaste, multiroleLinkToPaste, ...data } = source;
  return { ...data, stampImageCaches: {} };
};

const visibleObject: ShapeKind[] = [
  ShapeKind.Rect,
  ShapeKind.Square,
  ShapeKind.Oval,
  ShapeKind.Circle,
  ShapeKind.Triangle,
  ShapeKind.EquilateralTriangle,
  ShapeKind.Sticky,
  ShapeKind.Balloon,
  ShapeKind.StraightLine,
  ShapeKind.AxisAlignedStraightLine,
  ShapeKind.Polyline,
  ShapeKind.Polygon,
  ShapeKind.ImageFile,
  ShapeKind.MultiroleLink,
  ShapeKind.Stamp,
];

const visibleLearningPenObjects: ShapeKind[] = [
  ShapeKind.LearningPenRect,
];

export const initVisibleShapeKinds = (): ShapeKind[] => {
  return [
    ...visibleObject,
    ...visibleLearningPenObjects,
  ];
};

function setFreehandPenLineWidth(
  state: DrawingToolState,
  lineWidth: number,
  handerasingWidth: number,
): DrawingToolState {
  let newState = state;
  newState = updateDrawingAttrs(newState, { lineWidth }, [DrawingMode.Handwriting]);
  newState = updateDrawingAttrs(
    newState, { lineWidth: handerasingWidth }, [DrawingMode.Handerasing],
  );
  return newState;
}

function updateDrawingAttrs(
  state: DrawingToolState,
  attr: Partial<DrawingAttr>,
  modes: ReadonlyArray<DrawingMode>,
): DrawingToolState {
  let drawingAttrs = state.drawingAttrs;
  for (const mode of modes) {
    if (drawingAttrs[mode]) {
      drawingAttrs = { ...drawingAttrs, [mode]: { ...drawingAttrs[mode], ...attr } };
    }
  }
  return { ...state, drawingAttrs };
}

function setDrawingAttr(
  state: DrawingToolState,
  attr: Partial<DrawingAttr>,
  mode: DrawingMode,
): DrawingToolState {
  let drawingAttrs = state.drawingAttrs;
  if (drawingAttrs[mode]) {
    drawingAttrs = { ...drawingAttrs, [mode]: { ...drawingAttrs[mode], ...attr } };
  }
  return { ...state, mode, drawingAttrs };
}

function setVisibleShapeKinds(
  state: DrawingToolState,
  showObjects: boolean,
  showLearningPenObjects: boolean,
): DrawingToolState {
  const visibleObjectShapeKinds = showObjects ? visibleObject : [];
  const visibleLearningPenObjectShapeKinds =
    showLearningPenObjects ? visibleLearningPenObjects : [];
  const visibleShapeKinds = [...visibleObjectShapeKinds, ...visibleLearningPenObjectShapeKinds];
  return {
    ...state,
    visibleShapeKinds,
    showObjects,
    showLearningPenObjects,
    showHandwriting: showObjects,
  };
}

export default function drawingTool(
  state: DrawingToolState,
  action: DrawingToolAction.Action,
): DrawingToolState {
  switch (action.type) {
    case DrawingToolAction.ActionType.SET_MODE:
      return { ...state, mode: action.newMode };
    case DrawingToolAction.ActionType.SET_IMAGE_TO_PASTE:
      return { ...state, imageFileToPaste: action.newFile };
    case DrawingToolAction.ActionType.SET_PASTING_IMAGES:
      return { ...state, pastingImages: action.newPastingImages };
    case DrawingToolAction.ActionType.SET_MULTIROLE_LINK_TO_PASTE:
      return { ...state, multiroleLinkToPaste: action.newMultiroleLink };
    case DrawingToolAction.ActionType.SET_VISIBLE_SHAPE_KINDS:
      return { ...state, visibleShapeKinds: action.newVisibleShapeKinds };
    case DrawingToolAction.ActionType.SET_SHOW_HANDWRITING:
      return { ...state, showHandwriting: action.newShowHandwriting };
    case DrawingToolAction.ActionType.SET_ALPHA_ACTION:
      return { ...state, alpha: action.alpha };
    case DrawingToolAction.ActionType.SET_FREEHAND_PEN_LINE_WIDTH:
      return setFreehandPenLineWidth(state, action.lineWidth, action.handerasingWidth);
    case DrawingToolAction.ActionType.UPDATE_CURRET_DRAWING_ATTR:
      return updateDrawingAttrs(state, action.drawingAttrPartial, [state.mode]);
    case DrawingToolAction.ActionType.UPDATE_NOTE_DRAWING_ATTR:
      return updateDrawingAttrs(
        state,
        action.drawingAttrPartial,
        [DrawingMode.StickyDrawing, DrawingMode.BalloonDrawing],
      );
    case DrawingToolAction.ActionType.SET_DRAWING_ATTR:
      return setDrawingAttr(state, action.drawingAttrPartial, action.mode);
    case DrawingToolAction.ActionType.SHOW_OBJECTS:
      return setVisibleShapeKinds(state, action.showObjects, state.showLearningPenObjects);
    case DrawingToolAction.ActionType.SHOW_LEARNING_PEN_OBJECTS:
      return setVisibleShapeKinds(state, state.showObjects, action.showLearningPenObjects);
  }
}
